import { createSelector } from 'reselect';

import { AppState } from '../../store/store';
import { SupplierListItem } from '../models/supplier-list-item';
import { SupplierState } from './supplier-reducer';
import { createCategorySelector } from './articles-selectors';
import { localesSelector } from './configuration-selectors';
import { mapSupplierDetail } from '../models/mapper/supplier-detail-mapper';
import { mapSupplierListItem } from '../models/mapper/supplier-list-item-mapper';
import { mapSupplierSpecialOffer } from '../models/mapper/supplier-special-offer-mapper';

const supplierState = (state: AppState): SupplierState => state.supplier;

export const supplierSpecialOffersSelector = createSelector(localesSelector, supplierState, (locales, state) => ({
    supplierOffers: state.specialOffers.items.map(offer => mapSupplierSpecialOffer(locales, offer)),
    total: state.specialOffers.total,
}));

export const supplierDetailSelector = createSelector(
    localesSelector,
    supplierState,
    (locales, state) => state.detail && mapSupplierDetail(locales, state.detail)
);

export const supplierDirectoryDataSelector = createSelector(supplierState, state => {
    const items = state.suppliers.items.map(mapSupplierListItem);

    const grouped = items.reduce<Record<string, SupplierListItem[]>>((result, current) => {
        let firstLetter = current.name.charAt(0).toUpperCase();
        if (!isNaN(+firstLetter)) {
            firstLetter = '#';
        }
        if (!result[firstLetter]) {
            result[firstLetter] = [];
        }
        result[firstLetter].push(current);
        return result;
    }, {});

    const flat = Object.entries<SupplierListItem[]>(grouped).reduce<(string | SupplierListItem)[]>(
        (result, [key, items]) => {
            return [...result, key, ...items];
        },
        []
    );

    return {
        items: flat,
        total: state.suppliers.total,
        hasMore: state.suppliers.page < (state.suppliers.pages ?? Number.MAX_VALUE),
        nextPage: state.suppliers.page + 1,
        categoryCounts: state.suppliers.meta?.categories ?? [],
    };
});

export const supplierDirectoryCategorySelector = createSelector(supplierDirectoryDataSelector, directoryData =>
    createCategorySelector(directoryData.categoryCounts)
);
